import { FC, useState, useEffect, useCallback } from 'react';
import { Form, InputNumber, Button, Space, message, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import ProductSelector from '../ProductSelector';
import RealTimePriceMonitor from '../RealTimePriceMonitor';
import { setOffset, cancelOffset, fetchRealTimePrice, updateOffsetById, PriceOffset } from '../../api/priceOffset';

interface OffsetFormProps {
  onSuccess?: (result?: { 
    created?: PriceOffset; 
    updated?: { id: number; offsetValue: number; productCode: string }; 
  }) => void;
  onReset?: () => void;
  initialValues?: {
    productCode: string;
    offsetValue: number;
  };
  /** 更新模式相关参数 */
  updateMode?: {
    /** 价格偏移记录的ID */
    id: number;
    /** 是否为更新模式 */
    isUpdate: true;
  };
}

interface OffsetFormValues {
  productCode: string;
  offsetValue: number;
}

const OffsetForm: FC<OffsetFormProps> = ({ onSuccess, onReset, initialValues, updateMode }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string>();
  const [currentPrice, setCurrentPrice] = useState<number>(0);


  // 当initialValues变化时，设置表单值
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
      setSelectedProduct(initialValues.productCode);
    }
  }, [initialValues, form]);

  // 获取当前价格
  useEffect(() => {
    if (selectedProduct) {
      const fetchPrice = async () => {
        try {
          const priceData = await fetchRealTimePrice(selectedProduct);
          setCurrentPrice(priceData.controlPrice || priceData.price || 0);
        } catch (error) {
          console.error('获取当前价格失败', error);
        }
      };
      fetchPrice();
    }
  }, [selectedProduct]);

  const handleProductChange = (productCode: string) => {
    setSelectedProduct(productCode);
    // 当产品变更时，重置偏移量
    form.setFieldsValue({ offsetValue: undefined });
  };

  // 检查价格是否超出1%范围
  const checkPriceRange = (offsetValue: number): boolean => {
    if (!currentPrice || currentPrice === 0) return false;

    const targetPrice = currentPrice + offsetValue;
    const upperLimit = currentPrice * 1.01; // 上限：当前价格的101%
    const lowerLimit = currentPrice * 0.99; // 下限：当前价格的99%
    
    return targetPrice > upperLimit || targetPrice < lowerLimit;
  };

  // 显示二次确认弹窗
  const showConfirmModal = (values: OffsetFormValues) => {
    const targetPrice = currentPrice + values.offsetValue;
    const changePercent = (targetPrice - currentPrice) / currentPrice * 100;
    
    Modal.confirm({
      title: '价格变动超出1%',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>当前价格：{currentPrice.toFixed(4)}</p>
          <p>偏移量：{values.offsetValue > 0 ? '+' : ''}{values.offsetValue}</p>
          <p>目标价格：{targetPrice.toFixed(4)}</p>
          <p>变动幅度：{changePercent > 0 ? '+' : ''}{changePercent.toFixed(2)}%</p>
          <p style={{ color: '#ff4d4f', marginTop: 16 }}>
            价格变动幅度超过1%，请确认是否继续执行？
          </p>
        </div>
      ),
      okText: '确认执行',
      cancelText: '取消',
      onOk: () => executeOffset(values)
    });
  };

  // 防抖函数
  const debounce = useCallback((func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }, []);

  // 执行偏移设置
  const executeOffset = async (values: OffsetFormValues) => {
    setSubmitting(true);
    try {
      if (updateMode?.isUpdate) {
        // 更新模式：通过ID更新偏移量
        await updateOffsetById(updateMode.id, values.offsetValue);
        message.success('更新价格偏移成功');
        onSuccess?.({
          updated: {
            id: updateMode.id,
            offsetValue: values.offsetValue,
            productCode: values.productCode
          }
        });
      } else {
        // 创建模式：创建新的价格偏移
        const createdOffset = await setOffset(values.productCode, values.offsetValue);
        message.success('设置价格偏移成功');
        onSuccess?.({ created: createdOffset });
      }
    } catch (error) {
      if (updateMode?.isUpdate) {
        console.error('更新价格偏移失败', error);
      } else {
        console.error('设置价格偏移失败', error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitInternal = async (values: OffsetFormValues) => {
    // 检查价格范围
    if (checkPriceRange(values.offsetValue)) {
      showConfirmModal(values);
    } else {
      executeOffset(values);
    }
  };

  // 防抖处理的提交函数
  const handleSubmit = useCallback(
    debounce((values: OffsetFormValues) => {
      setSubmitDisabled(true);
      handleSubmitInternal(values).finally(() => {
        setTimeout(() => setSubmitDisabled(false), 2000); // 2秒防抖
      });
    }, 500),
    [updateMode, currentPrice, onSuccess] // 包含 executeOffset 中使用的关键依赖
  );

  const handleCancel = async () => {
    const productCode = form.getFieldValue('productCode');
    if (!productCode) {
      message.warning('请先选择产品');
      return;
    }

    setSubmitting(true);
    try {
      await cancelOffset(productCode);
      message.success('取消价格偏移成功');
      onSuccess?.();
    } catch (error) {
      console.error('取消价格偏移失败', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {selectedProduct && <RealTimePriceMonitor productCode={selectedProduct} />}
      
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="选择产品"
          name="productCode"
          rules={[{ required: true, message: '请选择产品' }]}
          extra={<span style={{ fontSize: '12px', color: '#999' }}>提示：休盘的产品已被过滤，不会在列表中显示</span>}
        >
          <ProductSelector
            onChange={handleProductChange}
            disabled={updateMode?.isUpdate}
          />
        </Form.Item>
        
        <Form.Item 
          label="偏移量" 
          name="offsetValue" 
          rules={[{ required: true, message: '请输入偏移量' }]}
        >
          <InputNumber 
            placeholder="请输入偏移量"
            style={{ width: '100%' }}
          />
        </Form.Item>
        
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting} disabled={submitDisabled}>
              {updateMode?.isUpdate ? '更新偏移' : '设置偏移'}
            </Button>
            {updateMode?.isUpdate && (
              <Button onClick={onReset}>
              退出编辑
            </Button>
            )}
          </Space>
        </Form.Item>
      </Form>
    </>
  );
};

export default OffsetForm; 